import React, { useState, useEffect } from 'react';
import { Layout, Card, Form, Input, Select, Button, message, Space, Typography, Row, Col, Switch, Steps, DatePicker, InputNumber, Upload, Checkbox, Tag } from 'antd';
import dayjs from 'dayjs';
import {
  UserOutlined,
  HomeOutlined,
  TeamOutlined,
  ArrowLeftOutlined,
  LoadingOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { API_BASE_URL } from '../api';
import Sidebar from './Sidebar';

const { Header, Content } = Layout;
const { Title } = Typography;
const { Option } = Select;
const { Step } = Steps;

const DEFAULT_STAFF_LOGIN_PASSWORD = 'Thinktech@123';

const AddRegularStaff = () => {
  const [loading, setLoading] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [salaryTemplates, setSalaryTemplates] = useState([]);
  const [attendanceTemplates, setAttendanceTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [pfEmployeePercent, setPfEmployeePercent] = useState(0);
  const [esiEmployeePercent, setEsiEmployeePercent] = useState(0);
  const [esiLimit, setEsiLimit] = useState(21000);
  const [ptSlabs, setPtSlabs] = useState([]);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const navigate = useNavigate();
  const [extraEarnings, setExtraEarnings] = useState([]); // { id, label, amount }
  const [extraDeductions, setExtraDeductions] = useState([]); // { id, label, amount }
  const [lastAddedEarningId, setLastAddedEarningId] = useState(null);
  const [lastAddedDeductionId, setLastAddedDeductionId] = useState(null);
  const location = useLocation();
  const editingStaff = location.state?.staff || null;
  const isEditing = !!editingStaff;
  const requiredRule = (message) => (isEditing ? [] : [{ required: true, message }]);
  const [isPercentageTemplate, setIsPercentageTemplate] = useState(false);
  const [templatePercentages, setTemplatePercentages] = useState({});

  const distributeFromBasic = (basicVal, pctMap = templatePercentages) => {
    const basic = Number(basicVal) || 0;
    if (!basic || basic <= 0) return;

    const calcAmount = (key) => {
      const pct = Number(pctMap[key] || 0);
      return Math.round((basic * pct) / 100);
    };

    const newValues = {};
    if (pctMap['HRA'] !== undefined) newValues.hra = calcAmount('HRA');
    if (pctMap['DA'] !== undefined) newValues.da = calcAmount('DA');
    if (pctMap['SPECIAL ALLOWANCE'] !== undefined) newValues.special_allowance = calcAmount('SPECIAL ALLOWANCE');
    if (pctMap['CONVEYANCE ALLOWANCE'] !== undefined) newValues.conveyance_allowance = calcAmount('CONVEYANCE ALLOWANCE');
    if (pctMap['MEDICAL ALLOWANCE'] !== undefined) newValues.medical_allowance = calcAmount('MEDICAL ALLOWANCE');
    if (pctMap['TRAVEL ALLOWANCE'] !== undefined) newValues.travel_allowance = calcAmount('TRAVEL ALLOWANCE');
    if (pctMap['OTHER ALLOWANCES'] !== undefined) newValues.other_allowances = calcAmount('OTHER ALLOWANCES');

    form.setFieldsValue(newValues);

    setExtraEarnings((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      return prev.map((item) => {
        const k = (item.label || '').toString().toUpperCase().trim();
        if (pctMap[k] !== undefined) {
          return {
            ...item,
            amount: Math.round((basic * Number(pctMap[k] || 0)) / 100),
          };
        }
        return item;
      });
    });
  };

  useEffect(() => {
    if (lastAddedEarningId) {
      const el = document.getElementById(`earn-${lastAddedEarningId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [lastAddedEarningId]);

  useEffect(() => {
    if (lastAddedDeductionId) {
      const el = document.getElementById(`ded-${lastAddedDeductionId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [lastAddedDeductionId]);

  const phoneWatch = Form.useWatch('phone', form);
  const salaryTemplateWatch = Form.useWatch('salaryTemplate', form);
  const basicSalaryWatch = Form.useWatch('basic_salary', form);
  const daWatch = Form.useWatch('da', form);
  const hraWatch = Form.useWatch('hra', form);
  const specialAllowanceWatch = Form.useWatch('special_allowance', form);
  const conveyanceAllowanceWatch = Form.useWatch('conveyance_allowance', form);
  const medicalAllowanceWatch = Form.useWatch('medical_allowance', form);
  const otherAllowancesWatch = Form.useWatch('other_allowances', form);
  const travelAllowanceWatch = Form.useWatch('travel_allowance', form);

  // Deductions watches
  const pfWatch = Form.useWatch('provident_fund_employee', form);
  const esiWatch = Form.useWatch('esi', form);
  const ptWatch = Form.useWatch('professional_tax', form);
  const itWatch = Form.useWatch('income_tax', form);
  const loanWatch = Form.useWatch('loan_deduction', form);
  const otherDedsWatch = Form.useWatch('other_deductions', form);

  const calcTotalEarnings = () =>
    Number(basicSalaryWatch || 0) +
    Number(hraWatch || 0) +
    Number(daWatch || 0) +
    Number(specialAllowanceWatch || 0) +
    Number(conveyanceAllowanceWatch || 0) +
    Number(medicalAllowanceWatch || 0) +
    Number(travelAllowanceWatch || 0) +
    Number(otherAllowancesWatch || 0) +
    (extraEarnings || []).reduce((sum, r) => sum + (parseFloat(r?.amount) || 0), 0);

  const calcTotalDeductions = () =>
    Number(pfWatch || 0) +
    Number(esiWatch || 0) +
    Number(ptWatch || 0) +
    Number(itWatch || 0) +
    Number(loanWatch || 0) +
    Number(otherDedsWatch || 0) +
    (extraDeductions || []).reduce((sum, r) => sum + (parseFloat(r?.amount) || 0), 0);

  const calcNetSalary = () => calcTotalEarnings() - calcTotalDeductions();

  const pickProfessionalTax = (total, slabs) => {
    const t = Number(total || 0);
    const arr = Array.isArray(slabs) ? slabs : [];
    for (const s of arr) {
      const min = Number(s?.min ?? 0);
      const maxRaw = s?.max;
      const max = maxRaw === null || maxRaw === undefined || maxRaw === '' ? null : Number(maxRaw);
      if (!Number.isFinite(min)) continue;
      if (max === null) {
        if (t >= min) return Number(s?.amount || 0);
      } else if (Number.isFinite(max)) {
        if (t >= min && t <= max) return Number(s?.amount || 0);
      }
    }
    return 0;
  };

  const step0HasErrors = () => {
    const errs = form.getFieldsError(['phone', 'email', 'salaryTemplate']);
    return errs.some(e => (e.errors || []).length > 0);
  };

  const fetchDepartments = async () => {
    try {
      const resp = await api.get('/admin/business-functions');
      const list = resp?.data?.data || [];
      const deptFn = list.find((f) => String(f.name || '').toLowerCase() === 'department');
      const values = Array.isArray(deptFn?.values) ? deptFn.values : [];
      const items = values
        .filter((v) => v && v.value)
        .map((v) => ({ id: v.id, name: v.value }));
      setDepartments(items);
    } catch (_) {
      setDepartments([]);
    }
  };

  const fetchShiftTemplates = async () => {
    try {
      const response = await api.get('/admin/shifts/templates');
      const list = response.data?.templates || [];
      setShiftTemplates(list);
    } catch (error) {
      // silent fail, keep static empty list
    }
  };

  const isStep0Complete = () => {
    if (isEditing) return true;
    const filled = !!(phoneWatch && salaryTemplateWatch);
    if (!filled) return false;
    return !step0HasErrors();
  };

  const isSalaryFieldVisible = (fieldKey) => {
    const toKV = (objOrArr) => {
      if (!objOrArr) return {};
      let src = objOrArr;
      if (typeof src === 'string') {
        try { src = JSON.parse(src); } catch (_) { return {}; }
      }
      if (Array.isArray(src)) {
        const out = {};
        src.flatMap((x) => (Array.isArray(x) ? x : [x])).forEach((it) => {
          const key = (it.key || it.name || '').toString();
          const val = Number(it.valueNumber ?? it.value_number ?? it.value ?? 0);
          if (key) out[key] = val;
        });
        return out;
      }
      return src && typeof src === 'object' ? src : {};
    };

    const earningsTpl = selectedTemplate ? toKV(selectedTemplate.earnings) : null;
    const deductionsTpl = selectedTemplate ? toKV(selectedTemplate.deductions) : null;
    const tplEmpty = (!earningsTpl || Object.keys(earningsTpl).length === 0) && (!deductionsTpl || Object.keys(deductionsTpl).length === 0);
    const showAll = !selectedTemplate || tplEmpty;
    if (showAll) return true;
    return !!(earningsTpl && Object.prototype.hasOwnProperty.call(earningsTpl, fieldKey));
  };

  const isStep1Complete = () => {
    if (isEditing) return true;
    const isFilled = (v) => v !== undefined && v !== null && String(v).trim() !== '';
    const requiredChecks = [];
    if (isSalaryFieldVisible('BASIC SALARY')) requiredChecks.push(isFilled(basicSalaryWatch));
    if (isSalaryFieldVisible('HRA')) requiredChecks.push(isFilled(hraWatch));
    if (isSalaryFieldVisible('DA')) requiredChecks.push(isFilled(daWatch));
    if (requiredChecks.length === 0) return true;
    return requiredChecks.every(Boolean);
  };

  // Fetch salary templates, attendance templates, and shift templates on component mount
  useEffect(() => {
    fetchSalaryTemplates();
    fetchAttendanceTemplates();
    fetchShiftTemplates();
    fetchDepartments();

    // Fetch education/experience if available
    if (isEditing) {
      if (editingStaff.profile?.education) {
        // education expect Array of {degree, institution, year}
      }
      if (editingStaff.profile?.experience) {
        // experience expect Array of {company, designation, duration}
      }
    }

    if (isEditing) {
      const pUrl = editingStaff.photoUrl || editingStaff.profile?.photoUrl;
      if (pUrl) {
        setPhotoUrl(pUrl);
      }
      console.log('Editing staff:', editingStaff);

      // Parse salaryValues if it's a string
      let sv = editingStaff.salaryValues || {};
      if (typeof sv === 'string') {
        try { sv = JSON.parse(sv); } catch (e) { sv = {}; }
      }

      const eb = sv.earnings || {};
      const db = sv.deductions || {};

      // Handle dynamic extras
      // Normalize keys for comparison: lowercase, remove spaces and underscores
      const normKey = (s) => (s || '').toString().toLowerCase().replace(/[_\s]+/g, '');
      const knownE = ['basic_salary', 'hra', 'da', 'special_allowance', 'conveyance_allowance', 'medical_allowance', 'travel_allowance', 'other_allowances'];
      const knownENorm = knownE.map(normKey);
      const ignoredE = ['totalearnings', 'totalincentives'];
      const extE = [];
      Object.entries(eb).forEach(([k, v]) => {
        const nk = normKey(k);
        if (!knownENorm.includes(nk) && !ignoredE.includes(nk)) {
          extE.push({ id: Date.now() + Math.random(), label: k.replace(/_/g, ' ').toUpperCase(), amount: v });
        }
      });
      setExtraEarnings(extE);

      const knownD = [
        'provident_fund', 'provident_fund_employee', 'provident_fund_employer',
        'esi', 'esi_employee', 'esi_employer',
        'professional_tax', 'professional_tax_employee', 'professional_tax_employer',
        'income_tax', 'income_tax_employee', 'income_tax_employer',
        'loan_deduction', 'other_deductions'
      ];
      const knownDNorm = knownD.map(normKey);
      const ignoredD = ['totaldeductions'];
      const extD = [];
      Object.entries(db).forEach(([k, v]) => {
        const nk = normKey(k);
        if (!knownDNorm.includes(nk) && !ignoredD.includes(nk)) {
          extD.push({ id: Date.now() + Math.random(), label: k.replace(/_/g, ' ').toUpperCase(), amount: v });
        }
      });
      setExtraDeductions(extD);

      const ob = editingStaff.openingBalance || 0;

      const parseJSON = (val) => {
        if (!val) return [];
        if (typeof val === 'string') {
          try {
            const p = JSON.parse(val);
            return Array.isArray(p) ? p : [];
          } catch (e) {
            return [];
          }
        }
        return Array.isArray(val) ? val : [];
      };

      const getVal = (obj, key, fallback) => {
        if (!obj) return fallback;
        const norm = (s) => (s || '').toLowerCase().replace(/[_\s]+/g, '');
        const target = norm(key);
        for (const [k, v] of Object.entries(obj)) {
          if (norm(k) === target) return v;
        }
        return fallback;
      };

      form.setFieldsValue({
        staffName: editingStaff.name,
        phone: editingStaff.phone,
        staffId: editingStaff.staffId,
        email: editingStaff.email,
        department: editingStaff.department,
        designation: editingStaff.designation,
        attendanceSettingTemplate: editingStaff.attendanceSettingTemplate,
        dateOfJoining: editingStaff.dateOfJoining ? dayjs(editingStaff.dateOfJoining) : null,
        staffType: editingStaff.staffType,
        salaryTemplate: editingStaff.salaryTemplateId,
        shiftSelection: editingStaff.shiftSelection ? Number(editingStaff.shiftSelection) : undefined,
        openingBalanceAmount: Math.abs(ob),
        openingBalanceType: ob < 0 ? 'pending' : 'advance',
        salaryDetailAccess: !!editingStaff.salaryDetailAccess,
        allowCurrentCycleSalaryAccess: !!editingStaff.allowCurrentCycleSalaryAccess,
        basic_salary: getVal(eb, 'basic_salary', editingStaff.basicSalary),
        hra: getVal(eb, 'hra', editingStaff.hra),
        da: getVal(eb, 'da', editingStaff.da),
        special_allowance: getVal(eb, 'special_allowance', editingStaff.specialAllowance),
        conveyance_allowance: getVal(eb, 'conveyance_allowance', editingStaff.conveyanceAllowance),
        medical_allowance: getVal(eb, 'medical_allowance', editingStaff.medicalAllowance),
        travel_allowance: getVal(eb, 'travel_allowance', 0),
        other_allowances: getVal(eb, 'other_allowances', editingStaff.otherAllowances),
        provident_fund_employee: getVal(db, 'provident_fund_employee', getVal(db, 'provident_fund', editingStaff.pfDeduction)),
        esi: getVal(db, 'esi_employee', getVal(db, 'esi', editingStaff.esiDeduction)),
        professional_tax: getVal(db, 'professional_tax', editingStaff.professionalTax),
        income_tax: getVal(db, 'income_tax', editingStaff.tdsDeduction),
        loan_deduction: getVal(db, 'loan_deduction', editingStaff.otherDeductions),
        other_deductions: getVal(db, 'other_deductions', editingStaff.otherDeductions),
        education: parseJSON(editingStaff.education || editingStaff.profile?.education),
        experience: parseJSON(editingStaff.experience || editingStaff.profile?.experience),
      });

      if (editingStaff.salaryTemplateId) {
        handleSalaryTemplateChange(editingStaff.salaryTemplateId, true);
      }
    }
  }, [isEditing, editingStaff]);

  const beforePhotoUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG file!');
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Image must smaller than 2MB!');
    }
    return isJpgOrPng && isLt2M;
  };

  const handlePhotoUpload = async (info) => {
    const { file } = info;
    if (file.status === 'uploading') {
      setPhotoLoading(true);
      return;
    }

    // Custom upload logic
    try {
      setPhotoLoading(true);
      const formData = new FormData();
      formData.append('photo', file.originFileObj || file);

      const response = await api.post('/admin/upload-profile-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setPhotoUrl(response.data.photoUrl);
        message.success('Photo uploaded successfully');
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      message.error('Failed to upload photo');
    } finally {
      setPhotoLoading(false);
    }
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_BASE_URL}${url}`;
  };

  const fetchSalaryTemplates = async () => {
    try {
      const response = await api.get('/admin/salary-templates');
      if (response.data.success) {
        setSalaryTemplates(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch salary templates:', error);
      message.error('Failed to load salary templates');
    }
  };

  const fetchAttendanceTemplates = async () => {
    try {
      const response = await api.get('/admin/attendance-templates');
      if (response.data.success) {
        setAttendanceTemplates(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch attendance templates:', error);
      message.error('Failed to load attendance templates');
    }
  };

  const handleSalaryTemplateChange = async (templateId, skipSetFields = false) => {
    try {
      const response = await api.get(`/admin/salary-templates/${templateId}`);
      const payload = response?.data?.data || response?.data || {};
      if (payload) {
        setSelectedTemplate(payload);

        const isPercent =
          payload.metadata?.calculationType === 'percentage' ||
          (Array.isArray(payload.earnings) && payload.earnings.some((e) => e && (e.type === 'percent' || e.type === 'percentage')));
        setIsPercentageTemplate(Boolean(isPercent));

        const pctMap = {};
        let rawEarn = payload.earnings;
        if (typeof rawEarn === 'string') {
          try { rawEarn = JSON.parse(rawEarn); } catch (_) { rawEarn = []; }
        }
        if (Array.isArray(rawEarn)) {
          rawEarn.flatMap((x) => (Array.isArray(x) ? x : [x])).forEach((it) => {
            const k = (it?.key || it?.name || it?.label || '').toString().toUpperCase().trim();
            if (k) pctMap[k] = Number(it?.valueNumber ?? it?.value ?? 0);
          });
        } else if (rawEarn && typeof rawEarn === 'object') {
          Object.entries(rawEarn).forEach(([k, v]) => {
            const up = k.toString().toUpperCase().trim();
            pctMap[up] = typeof v === 'object' ? Number(v?.valueNumber ?? v?.value ?? 0) : Number(v || 0);
          });
        }
        setTemplatePercentages(pctMap);
        const toKV = (objOrArr) => {
          if (!objOrArr) return {};
          let src = objOrArr;
          if (typeof src === 'string') {
            try { src = JSON.parse(src); } catch (_) { return {}; }
          }
          if (Array.isArray(src)) {
            const out = {};
            src.forEach((it) => {
              const key = (it.key || it.name || '').toString();
              const val = Number(it.valueNumber ?? it.value ?? 0);
              if (key) out[key] = val;
            });
            return out;
          }
          return src && typeof src === 'object' ? src : {};
        };

        const earnings = toKV(payload.earnings);
        const deductions = toKV(payload.deductions);

        const basic = Number(earnings['BASIC SALARY'] || 0);
        const da = Number(earnings['DA'] || 0);

        // PF % extraction must work for array / nested array / stringified JSON / object
        let dedSrc = payload.deductions;
        if (typeof dedSrc === 'string') {
          try { dedSrc = JSON.parse(dedSrc); } catch (_) { dedSrc = null; }
        }
        let pfPercent = 0;
        if (Array.isArray(dedSrc)) {
          const deductionsArray = dedSrc.flatMap((x) => (Array.isArray(x) ? x : [x]));
          const pfEmployeeItem = deductionsArray.find(d => (d?.key || d?.name) === 'PROVIDENT_FUND_EMPLOYEE');
          pfPercent = Number(pfEmployeeItem?.valueNumber ?? pfEmployeeItem?.value_number ?? pfEmployeeItem?.value ?? 0) || 0;
        } else if (dedSrc && typeof dedSrc === 'object') {
          // If backend returns object, it could be either a plain number or an object with value/valueNumber
          const v = dedSrc.PROVIDENT_FUND_EMPLOYEE;
          pfPercent = Number(v?.valueNumber ?? v?.value_number ?? v?.value ?? v ?? 0) || 0;
        }
        setPfEmployeePercent(pfPercent);

        let esiPercent = 0;
        let loadedEsiLimit = 21000;
        if (Array.isArray(dedSrc)) {
          const deductionsArray = dedSrc.flatMap((x) => (Array.isArray(x) ? x : [x]));
          const esiEmployeeItem = deductionsArray.find(d => (d?.key || d?.name) === 'ESI_EMPLOYEE');
          if (esiEmployeeItem) {
            esiPercent = Number(esiEmployeeItem?.valueNumber ?? esiEmployeeItem?.value_number ?? esiEmployeeItem?.value ?? 0) || 0;
            if (esiEmployeeItem?.meta?.salaryLimit !== undefined && esiEmployeeItem?.meta?.salaryLimit !== null) {
              loadedEsiLimit = Number(esiEmployeeItem.meta.salaryLimit);
            }
          } else {
            const legacyEsiItem = deductionsArray.find(d => (d?.key || d?.name) === 'ESI');
            esiPercent = Number(legacyEsiItem?.valueNumber ?? legacyEsiItem?.value_number ?? legacyEsiItem?.value ?? 0) || 0;
            if (legacyEsiItem?.meta?.salaryLimit !== undefined && legacyEsiItem?.meta?.salaryLimit !== null) {
              loadedEsiLimit = Number(legacyEsiItem.meta.salaryLimit);
            }
          }
        } else if (dedSrc && typeof dedSrc === 'object') {
          const v = dedSrc.ESI_EMPLOYEE ?? dedSrc.ESI;
          esiPercent = Number(v?.valueNumber ?? v?.value_number ?? v?.value ?? v ?? 0) || 0;
          if (v?.meta?.salaryLimit !== undefined && v?.meta?.salaryLimit !== null) {
            loadedEsiLimit = Number(v.meta.salaryLimit);
          }
        }
        setEsiEmployeePercent(esiPercent);
        setEsiLimit(loadedEsiLimit);

        let slabs = [];
        if (Array.isArray(dedSrc)) {
          const deductionsArray = dedSrc.flatMap((x) => (Array.isArray(x) ? x : [x]));
          const ptItem = deductionsArray.find(d => (d?.key || d?.name) === 'PROFESSIONAL TAX' || (d?.key || d?.name) === 'PROFESSIONAL_TAX');
          slabs = Array.isArray(ptItem?.meta?.slabs) ? ptItem.meta.slabs : [];
        } else if (dedSrc && typeof dedSrc === 'object') {
          const v = dedSrc['PROFESSIONAL TAX'] ?? dedSrc.PROFESSIONAL_TAX;
          slabs = Array.isArray(v?.meta?.slabs) ? v.meta.slabs : [];
        }
        setPtSlabs(slabs);

        const pfBase = basic + da;
        const pfEmployeeAmount = Math.round(pfBase * (pfPercent / 100) * 100) / 100;

        const totalEarnings =
          Number(basic || 0) +
          Number(earnings['HRA'] || 0) +
          Number(da || 0) +
          Number(earnings['SPECIAL ALLOWANCE'] || 0) +
          Number(earnings['CONVEYANCE ALLOWANCE'] || 0) +
          Number(earnings['MEDICAL ALLOWANCE'] || 0) +
          Number(earnings['TRAVEL ALLOWANCE'] || 0) +
          Number(earnings['OTHER ALLOWANCES'] || 0);
        const esiEmployeeAmount = totalEarnings > loadedEsiLimit ? 0 : Math.round(totalEarnings * (esiPercent / 100) * 100) / 100;
        const professionalTaxAmount = Math.round(pickProfessionalTax(totalEarnings, slabs) * 100) / 100;

        if (!skipSetFields) {
          if (isPercent) {
            const currentBasic = form.getFieldValue('basic_salary');
            if (currentBasic && Number(currentBasic) > 0) {
              distributeFromBasic(currentBasic, pctMap);
            } else {
              form.setFieldsValue({
                basic_salary: undefined,
                hra: undefined,
                da: undefined,
                special_allowance: undefined,
                conveyance_allowance: undefined,
                medical_allowance: undefined,
                travel_allowance: undefined,
                other_allowances: undefined,
                provident_fund_employee: 0,
                provident_fund_employer: 0,
                esi: 0,
                professional_tax: 0,
                income_tax: Number(deductions['INCOME TAX']?.valueNumber || 0),
                loan_deduction: Number(deductions['LOAN DEDUCTION']?.valueNumber || 0),
                other_deductions: Number(deductions['OTHER DEDUCTIONS']?.valueNumber || 0),
              });
            }
          } else {
            form.setFieldsValue({
              basic_salary: basic,
              hra: Number(earnings['HRA'] || 0),
              da: da,
              special_allowance: Number(earnings['SPECIAL ALLOWANCE'] || 0),
              conveyance_allowance: Number(earnings['CONVEYANCE ALLOWANCE'] || 0),
              medical_allowance: Number(earnings['MEDICAL ALLOWANCE'] || 0),
              travel_allowance: Number(earnings['TRAVEL ALLOWANCE'] || 0),
              other_allowances: Number(earnings['OTHER ALLOWANCES'] || 0),
              provident_fund_employee: pfEmployeeAmount,
              provident_fund_employer: 0,
              esi: esiEmployeeAmount,
              professional_tax: professionalTaxAmount,
              income_tax: Number(deductions['INCOME TAX']?.valueNumber || 0),
              loan_deduction: Number(deductions['LOAN DEDUCTION']?.valueNumber || 0),
              other_deductions: Number(deductions['OTHER DEDUCTIONS']?.valueNumber || 0),
            });
          }
        } else {
          if (isPercent) {
            const currentBasic = form.getFieldValue('basic_salary');
            if (currentBasic && Number(currentBasic) > 0) {
              distributeFromBasic(currentBasic, pctMap);
            }
          }
        }

        // Extract and populate dynamic/custom earnings from the template (e.g. TIFFIN)
        const normKey = (s) => (s || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
        const knownE = ['basic_salary', 'hra', 'da', 'special_allowance', 'conveyance_allowance', 'medical_allowance', 'travel_allowance', 'other_allowances'];
        const knownENorm = knownE.map(normKey);
        const ignoredE = ['totalearnings', 'totalincentives'];

        let tplEarningsList = [];
        if (typeof payload.earnings === 'string') {
          try { tplEarningsList = JSON.parse(payload.earnings); } catch (_) { tplEarningsList = []; }
        } else if (Array.isArray(payload.earnings)) {
          tplEarningsList = payload.earnings;
        } else if (payload.earnings && typeof payload.earnings === 'object') {
          tplEarningsList = Object.entries(payload.earnings).map(([k, v]) => ({
            key: k,
            label: k,
            valueNumber: typeof v === 'object' ? (v?.valueNumber ?? v?.value ?? 0) : v
          }));
        }
        if (!Array.isArray(tplEarningsList)) tplEarningsList = [];
        tplEarningsList = tplEarningsList.flatMap(x => (Array.isArray(x) ? x : [x]));

        const customTplEarnings = [];
        tplEarningsList.forEach((it) => {
          const rawKey = (it.key || it.name || it.label || '').toString().trim();
          const nk = normKey(rawKey);
          if (rawKey && !knownENorm.includes(nk) && !ignoredE.includes(nk)) {
            let existingAmount = undefined;
            if (editingStaff) {
              let sv = editingStaff.salaryValues || {};
              if (typeof sv === 'string') {
                try { sv = JSON.parse(sv); } catch (_) { sv = {}; }
              }
              const eb = sv.earnings || {};
              for (const [k, v] of Object.entries(eb)) {
                if (normKey(k) === nk) {
                  existingAmount = v;
                  break;
                }
              }
            }
            const val = existingAmount !== undefined ? existingAmount : (it.valueNumber ?? it.value ?? '');
            customTplEarnings.push({
              id: Date.now() + Math.random(),
              label: it.label || rawKey,
              amount: val,
              fromTemplate: true
            });
          }
        });

        setExtraEarnings((prev) => {
          const merged = [...customTplEarnings];
          (prev || []).forEach((p) => {
            if (!p.fromTemplate && !merged.some((m) => normKey(m.label) === normKey(p.label))) {
              merged.push(p);
            }
          });
          return merged;
        });

        // Extract and populate dynamic/custom deductions from the template
        const knownD = [
          'provident_fund', 'provident_fund_employee', 'provident_fund_employer',
          'esi', 'esi_employee', 'esi_employer',
          'professional_tax', 'professional_tax_employee', 'professional_tax_employer',
          'income_tax', 'income_tax_employee', 'income_tax_employer',
          'loan_deduction', 'other_deductions'
        ];
        const knownDNorm = knownD.map(normKey);
        const ignoredD = ['totaldeductions'];

        let tplDeductionsList = [];
        if (typeof payload.deductions === 'string') {
          try { tplDeductionsList = JSON.parse(payload.deductions); } catch (_) { tplDeductionsList = []; }
        } else if (Array.isArray(payload.deductions)) {
          tplDeductionsList = payload.deductions;
        } else if (payload.deductions && typeof payload.deductions === 'object') {
          tplDeductionsList = Object.entries(payload.deductions).map(([k, v]) => ({
            key: k,
            label: k,
            valueNumber: typeof v === 'object' ? (v?.valueNumber ?? v?.value ?? 0) : v
          }));
        }
        if (!Array.isArray(tplDeductionsList)) tplDeductionsList = [];
        tplDeductionsList = tplDeductionsList.flatMap(x => (Array.isArray(x) ? x : [x]));

        const customTplDeductions = [];
        tplDeductionsList.forEach((it) => {
          const rawKey = (it.key || it.name || it.label || '').toString().trim();
          const nk = normKey(rawKey);
          if (rawKey && !knownDNorm.includes(nk) && !ignoredD.includes(nk)) {
            let existingAmount = undefined;
            if (editingStaff) {
              let sv = editingStaff.salaryValues || {};
              if (typeof sv === 'string') {
                try { sv = JSON.parse(sv); } catch (_) { sv = {}; }
              }
              const db = sv.deductions || {};
              for (const [k, v] of Object.entries(db)) {
                if (normKey(k) === nk) {
                  existingAmount = v;
                  break;
                }
              }
            }
            const val = existingAmount !== undefined ? existingAmount : (it.valueNumber ?? it.value ?? '');
            customTplDeductions.push({
              id: Date.now() + Math.random(),
              label: it.label || rawKey,
              amount: val,
              fromTemplate: true
            });
          }
        });

        setExtraDeductions((prev) => {
          const merged = [...customTplDeductions];
          (prev || []).forEach((p) => {
            if (!p.fromTemplate && !merged.some((m) => normKey(m.label) === normKey(p.label))) {
              merged.push(p);
            }
          });
          return merged;
        });
      }
    } catch (error) {
      console.error('Failed to fetch template:', error);
    }
  };

  useEffect(() => {
    if (!selectedTemplate) return;
    const basic = Number(basicSalaryWatch || 0);
    const da = Number(daWatch || 0);
    const base = basic + da;
    const percent = Number(pfEmployeePercent || 0);
    const amount = Math.round(base * (percent / 100) * 100) / 100;
    form.setFieldsValue({
      provident_fund_employee: amount,
      provident_fund_employer: 0,
    });
  }, [selectedTemplate, basicSalaryWatch, daWatch, pfEmployeePercent]);

  useEffect(() => {
    if (!selectedTemplate) return;
    const total = calcTotalEarnings();
    const percent = Number(esiEmployeePercent || 0);
    const amount = total > esiLimit ? 0 : Math.round(total * (percent / 100) * 100) / 100;
    form.setFieldsValue({ esi: amount });
  }, [
    selectedTemplate,
    basicSalaryWatch,
    hraWatch,
    daWatch,
    specialAllowanceWatch,
    conveyanceAllowanceWatch,
    medicalAllowanceWatch,
    travelAllowanceWatch,
    otherAllowancesWatch,
    extraEarnings,
    esiEmployeePercent,
    esiLimit,
    form,
  ]);

  useEffect(() => {
    if (!selectedTemplate) return;
    const total = calcTotalEarnings();
    const amount = Math.round(pickProfessionalTax(total, ptSlabs) * 100) / 100;
    form.setFieldsValue({ professional_tax: amount });
  }, [
    selectedTemplate,
    basicSalaryWatch,
    hraWatch,
    daWatch,
    specialAllowanceWatch,
    conveyanceAllowanceWatch,
    medicalAllowanceWatch,
    otherAllowancesWatch,
    extraEarnings,
    ptSlabs,
    form,
  ]);

  const steps = [
    {
      title: 'Basic Details',
      content: 'basic',
    },
    {
      title: 'Salary Details',
      content: 'salary',
    },
    {
      title: 'Education & Experience',
      content: 'education_experience',
    },
  ];

  const next = () => {
    setCurrentStep(currentStep + 1);
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      // Merge all current form values to avoid losing unmounted fields
      const allValues = { ...form.getFieldsValue(true), ...values };
      // Ensure critical fields exist even if Step 1 is unmounted
      const phoneVal = (allValues.phone || '').toString().trim();
      const emailVal = (allValues.email || '').toString().trim();
      const salaryTemplateVal = allValues.salaryTemplate;

      if (!isEditing && !phoneVal) {
        setLoading(false);
        message.error('Phone is required');
        setCurrentStep(0);
        return;
      }
      if (!isEditing && !salaryTemplateVal) {
        setLoading(false);
        message.error('Salary template is required');
        setCurrentStep(0);
        return;
      }

      // Calculate total salary components
      const basic_salary = parseFloat(allValues.basic_salary) || 0;
      const hra = parseFloat(allValues.hra) || 0;
      const da = parseFloat(allValues.da) || 0;
      const special_allowance = parseFloat(allValues.special_allowance) || 0;
      const conveyance_allowance = parseFloat(allValues.conveyance_allowance) || 0;
      const medical_allowance = parseFloat(allValues.medical_allowance) || 0;
      const other_allowances = parseFloat(allValues.other_allowances) || 0;

      // Calculate deductions
      const provident_fund_employee = parseFloat(allValues.provident_fund_employee) || 0;
      const esi = parseFloat(allValues.esi) || 0;
      const professional_tax = parseFloat(allValues.professional_tax) || 0;
      const income_tax = parseFloat(allValues.income_tax) || 0;
      const loan_deduction = parseFloat(allValues.loan_deduction) || 0;
      const other_deductions = parseFloat(allValues.other_deductions) || 0;

      // Prepare salary values based on selected template fields
      const toKV = (objOrArr) => {
        if (!objOrArr) return {};
        let src = objOrArr;
        if (typeof src === 'string') {
          try { src = JSON.parse(src); } catch (_) { return {}; }
        }
        if (Array.isArray(src)) {
          const out = {};
          src.forEach((it) => {
            const key = (it.key || it.name || '').toString();
            const val = Number(it.valueNumber ?? it.value ?? 0);
            if (key) out[key] = val;
          });
          return out;
        }
        return src && typeof src === 'object' ? src : {};
      };
      const earningsTpl = selectedTemplate ? toKV(selectedTemplate.earnings) : null;
      const deductionsTpl = selectedTemplate ? toKV(selectedTemplate.deductions) : null;
      const tplEmpty = (!earningsTpl || Object.keys(earningsTpl).length === 0) && (!deductionsTpl || Object.keys(deductionsTpl).length === 0);
      const knownE = ['BASIC SALARY', 'HRA', 'DA', 'SPECIAL ALLOWANCE', 'CONVEYANCE ALLOWANCE', 'MEDICAL ALLOWANCE', 'TRAVEL ALLOWANCE', 'OTHER ALLOWANCES'];
      const knownD = ['PROVIDENT FUND', 'ESI', 'PROFESSIONAL TAX', 'INCOME TAX', 'LOAN DEDUCTION', 'OTHER DEDUCTIONS'];
      const hasKnownE = earningsTpl && knownE.some(k => Object.prototype.hasOwnProperty.call(earningsTpl, k));
      const hasKnownD = deductionsTpl && knownD.some(k => Object.prototype.hasOwnProperty.call(deductionsTpl, k));
      const showAll = !selectedTemplate || tplEmpty || (!hasKnownE && !hasKnownD);
      const showE = (k) => showAll || (earningsTpl && Object.prototype.hasOwnProperty.call(earningsTpl, k));
      const showD = (k) => {
        if (showAll) return true;
        if (!selectedTemplate) return false;

        // Special check for PROVIDENT FUND - check in original template array
        if (k === 'PROVIDENT FUND') {
          let src = selectedTemplate.deductions;
          if (typeof src === 'string') {
            try { src = JSON.parse(src); } catch (_) { src = null; }
          }
          const pfKeys = ['PROVIDENT_FUND_EMPLOYEE', 'PROVIDENT_FUND', 'PROVIDENT FUND', 'PROVIDENT_FUND_EMPLOYER'];
          const norm = (v) => (v || '').toString().trim().toUpperCase();
          const pfKeysNorm = pfKeys.map(norm);
          if (Array.isArray(src)) {
            const arr = src.flatMap((x) => (Array.isArray(x) ? x : [x]));
            return arr.some((d) => pfKeysNorm.includes(norm(d?.key || d?.name)));
          }
          if (src && typeof src === 'object') {
            return Object.keys(src).some((key) => pfKeysNorm.includes(norm(key)));
          }
          return false;
        }

        if (k === 'ESI') {
          let src = selectedTemplate.deductions;
          if (typeof src === 'string') {
            try { src = JSON.parse(src); } catch (_) { src = null; }
          }
          const esiKeys = ['ESI_EMPLOYEE', 'ESI'];
          if (Array.isArray(src)) {
            const arr = src.flatMap((x) => (Array.isArray(x) ? x : [x]));
            return esiKeys.some((key) => arr.some((d) => (d?.key || d?.name) === key));
          }
          if (src && typeof src === 'object') {
            return esiKeys.some((key) => Object.prototype.hasOwnProperty.call(src, key));
          }
          return false;
        }

        if (k === 'PROFESSIONAL TAX') {
          let src = selectedTemplate.deductions;
          if (typeof src === 'string') {
            try { src = JSON.parse(src); } catch (_) { src = null; }
          }
          const ptKeys = ['PROFESSIONAL TAX', 'PROFESSIONAL_TAX'];
          if (Array.isArray(src)) {
            const arr = src.flatMap((x) => (Array.isArray(x) ? x : [x]));
            return ptKeys.some((key) => arr.some((d) => (d?.key || d?.name) === key));
          }
          if (src && typeof src === 'object') {
            return ptKeys.some((key) => Object.prototype.hasOwnProperty.call(src, key));
          }
          return false;
        }

        // For other deductions, check in converted object
        if (!deductionsTpl) return false;
        return Object.prototype.hasOwnProperty.call(deductionsTpl, k);
      };

      const salaryValues = { earnings: {}, deductions: {} };
      if (showE('BASIC SALARY')) salaryValues.earnings.basic_salary = basic_salary;
      if (showE('HRA')) salaryValues.earnings.hra = hra;
      if (showE('DA')) salaryValues.earnings.da = da;
      if (showE('SPECIAL ALLOWANCE')) salaryValues.earnings.special_allowance = special_allowance;
      if (showE('CONVEYANCE ALLOWANCE')) salaryValues.earnings.conveyance_allowance = conveyance_allowance;
      if (showE('MEDICAL ALLOWANCE')) salaryValues.earnings.medical_allowance = medical_allowance;
      if (showE('TRAVEL ALLOWANCE')) salaryValues.earnings.travel_allowance = parseFloat(allValues.travel_allowance) || 0;
      if (showE('OTHER ALLOWANCES')) salaryValues.earnings.other_allowances = other_allowances;

      if (showD('PROVIDENT FUND') || (provident_fund_employee !== 0)) { salaryValues.deductions.provident_fund = provident_fund_employee; }
      if (showD('ESI') || (esi !== 0)) salaryValues.deductions.esi = esi;
      if (showD('PROFESSIONAL TAX') || (professional_tax !== 0)) salaryValues.deductions.professional_tax = professional_tax;
      if (showD('INCOME TAX') || (income_tax !== 0)) salaryValues.deductions.income_tax = income_tax;
      if (showD('LOAN DEDUCTION') || (loan_deduction !== 0)) salaryValues.deductions.loan_deduction = loan_deduction;
      if (showD('OTHER DEDUCTIONS') || (other_deductions !== 0)) salaryValues.deductions.other_deductions = other_deductions;

      // Add dynamic extras
      const toKey = (s) => (s || '')
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      extraEarnings.forEach(row => {
        const k = toKey(row.label || 'earning');
        if (k) salaryValues.earnings[k] = parseFloat(row.amount) || 0;
      });
      extraDeductions.forEach(row => {
        const k = toKey(row.label || 'deduction');
        if (k) salaryValues.deductions[k] = parseFloat(row.amount) || 0;
      });

      // Normalize date fields
      const salaryCycleDateStr = Array.isArray(allValues.salaryCycleDate) && allValues.salaryCycleDate.length
        ? allValues.salaryCycleDate[0]?.format?.('YYYY-MM-DD') || null
        : null;

      // Opening balance compute
      const obType = allValues.openingBalanceType || 'advance';
      const obAmount = parseFloat(allValues.openingBalanceAmount) || 0;
      const openingBalanceComputed = obType === 'pending' ? -Math.abs(obAmount) : Math.abs(obAmount);

      // Create staff member with all details
      const staffData = {
        // Basic Details
        staffId: allValues.staffId,
        phone: phoneVal,
        name: allValues.staffName,
        email: emailVal,
        password: isEditing ? undefined : DEFAULT_STAFF_LOGIN_PASSWORD,

        // Additional Form Fields
        department: allValues.department,
        designation: allValues.designation,
        attendanceSettingTemplate: allValues.attendanceSettingTemplate,
        salaryCycleDate: salaryCycleDateStr,
        dateOfJoining: allValues.dateOfJoining ? allValues.dateOfJoining.format('YYYY-MM-DD') : null,
        staffType: allValues.staffType,
        shiftSelection: allValues.shiftSelection,
        openingBalance: openingBalanceComputed,
        openingBalanceType: obType,
        salaryDetailAccess: !!allValues.salaryDetailAccess,
        allowCurrentCycleSalaryAccess: !!allValues.allowCurrentCycleSalaryAccess,
        active: allValues.active !== undefined
          ? allValues.active !== false
          : (isEditing ? (editingStaff?.active !== false) : true),

        // Salary Template
        salaryTemplateId: salaryTemplateVal,

        // Salary Values (structured for backend)
        salaryValues,
        photoUrl,
        education: allValues.education || [],
        experience: allValues.experience || [],
        sendOfferLetter: !!allValues.sendOfferLetter
      };

      const response = isEditing
        ? await api.put(`/admin/staff/${editingStaff.id}`, staffData)
        : await api.post('/admin/staff', staffData);

      if (response.data.success) {
        // Assign shift if selected
        const createdStaffId = isEditing ? editingStaff.id : (response.data?.staff?.id || response.data?.data?.id);
        const selectedShiftId = allValues.shiftSelection;
        if (createdStaffId && selectedShiftId) {
          try {
            const today = new Date();
            const yyyy = today.getFullYear();
            const mm = String(today.getMonth() + 1).padStart(2, '0');
            const dd = String(today.getDate()).padStart(2, '0');
            const effectiveFrom = `${yyyy}-${mm}-${dd}`;
            await api.post('/admin/shifts/assign', { userId: createdStaffId, shiftTemplateId: Number(selectedShiftId), effectiveFrom });
          } catch (_) {
            // ignore assign error, continue
          }
        }
        message.success(isEditing ? 'Staff member updated successfully' : 'Regular staff member added successfully with all salary details');
        navigate('/staff-management');
      } else {
        message.error(response.data.message || 'Failed to save staff member');
      }
    } catch (error) {
      console.error('Failed to save staff member:', error);
      message.error(error.response?.data?.message || 'Failed to save staff member');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/staff-management');
  };

  const renderStepContent = () => {
    // Determine which fields to show based on selected template
    const toKV = (objOrArr) => {
      if (!objOrArr) return {};
      let src = objOrArr;
      if (typeof src === 'string') {
        try { src = JSON.parse(src); } catch (_) { return {}; }
      }
      if (Array.isArray(src)) {
        const out = {};
        src.flatMap((x) => (Array.isArray(x) ? x : [x])).forEach((it) => {
          const key = (it.key || it.name || '').toString();
          const val = Number(it.valueNumber ?? it.value_number ?? it.value ?? 0);
          if (key) out[key] = val;
        });
        return out;
      }
      return src && typeof src === 'object' ? src : {};
    };
    const earningsTpl = selectedTemplate ? toKV(selectedTemplate.earnings) : null;
    const deductionsTpl = selectedTemplate ? toKV(selectedTemplate.deductions) : null;
    const tplEmpty = (!earningsTpl || Object.keys(earningsTpl).length === 0) && (!deductionsTpl || Object.keys(deductionsTpl).length === 0);
    const showAll = !selectedTemplate || tplEmpty;
    const showE = (k) => showAll || (earningsTpl && Object.prototype.hasOwnProperty.call(earningsTpl, k));
    const showD = (k) => {
      if (showAll) return true;
      if (!selectedTemplate) return false;

      if (k === 'PROVIDENT FUND') {
        const pfKeys = ['PROVIDENT_FUND_EMPLOYEE', 'PROVIDENT_FUND', 'PROVIDENT FUND', 'PROVIDENT_FUND_EMPLOYER'];
        const norm = (v) => (v || '').toString().trim().toUpperCase();
        const pfKeysNorm = pfKeys.map(norm);

        let src = selectedTemplate.deductions;
        if (typeof src === 'string') {
          try { src = JSON.parse(src); } catch (_) { src = null; }
        }

        if (Array.isArray(src)) {
          const arr = src.flatMap((x) => (Array.isArray(x) ? x : [x]));
          return arr.some((d) => pfKeysNorm.includes(norm(d?.key || d?.name)));
        }

        if (src && typeof src === 'object') {
          return Object.keys(src).some((key) => pfKeysNorm.includes(norm(key)));
        }

        return false;
      }

      if (k === 'ESI') {
        const esiKeys = ['ESI_EMPLOYEE', 'ESI'];

        let src = selectedTemplate.deductions;
        if (typeof src === 'string') {
          try { src = JSON.parse(src); } catch (_) { src = null; }
        }

        if (Array.isArray(src)) {
          const arr = src.flatMap((x) => (Array.isArray(x) ? x : [x]));
          return esiKeys.some((key) => arr.some((d) => (d?.key || d?.name) === key));
        }

        if (src && typeof src === 'object') {
          return esiKeys.some((key) => Object.prototype.hasOwnProperty.call(src, key));
        }

        return false;
      }

      return !!(deductionsTpl && Object.prototype.hasOwnProperty.call(deductionsTpl, k));
    };
    switch (currentStep) {
      case 0:
        return (
          <div style={{ padding: '20px' }}>
            <Row gutter={[16, 16]}>
              {/* Basic Details Card */}
              <Col span={24}>
                <Card
                  title="Basic Details"
                  style={{
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  <Row gutter={[16, 16]}>
                    <Col span={24} style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                      <Upload
                        name="avatar"
                        listType="picture-card"
                        className="avatar-uploader"
                        showUploadList={false}
                        beforeUpload={beforePhotoUpload}
                        customRequest={handlePhotoUpload}
                      >
                        {photoUrl ? (
                          <img
                            src={getFullImageUrl(photoUrl)}
                            alt="avatar"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
                          />
                         ) : (
                          <div>
                            {photoLoading ? <LoadingOutlined /> : <PlusOutlined />}
                            <div style={{ marginTop: 8, fontSize: '11px', color: '#8c8c8c' }}>Upload Photo<br/>(JPG/PNG, Max 2MB)</div>
                          </div>
                        )}
                      </Upload>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="staffName"
                        label="Staff Name"
                      >
                        <Input placeholder="Enter staff name" style={{ height: '40px' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="phone"
                        label="Phone Number"
                        validateTrigger="onChange"
                        rules={[
                          ...requiredRule('Please enter phone number'),
                          { len: 10, message: 'Please enter exactly 10 digits' },
                          { pattern: /^\d+$/, message: 'Please enter only digits' }
                        ]}
                      >
                        <Input 
                          placeholder="Enter phone number" 
                          style={{ height: '40px' }} 
                          maxLength={10}
                          onKeyDown={(e) => {
                            if (e.key === ' ') e.preventDefault();
                          }}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\s/g, '');
                            form.setFieldsValue({ phone: value });
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="staffId"
                        label="Staff ID"
                      >
                        <Input placeholder="Enter staff ID" style={{ height: '40px' }} />
                      </Form.Item>
                    </Col>
                    {/* <Col span={12}>
                      <Form.Item
                        name="attendanceSettingTemplate"
                        label="Attendance Setting Template"
                      >
                        <Select placeholder="Select attendance template" style={{ height: '40px' }}>
                          {attendanceTemplates.map(template => (
                            <Option key={template.id} value={template.id}>
                              {template.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col> */}
                    <Col span={12}>
                      <Form.Item
                        name="department"
                        label="Department"
                      >
                        <Select placeholder="Select department" style={{ height: '40px' }}>
                          {departments.length > 0 && departments.map((d) => (
                            <Option key={d.id || d.name} value={d.name}>{d.name}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="dateOfJoining"
                        label="Joining Date"
                      >
                        <DatePicker style={{ width: '100%', height: '40px' }} format="DD-MM-YYYY" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              </Col>

              {/* Salary Details Card */}
              <Col span={24}>
                <Card
                  title="Salary Details"
                  style={{
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  <Row gutter={[16, 16]}>
                    {/* <Col span={12}>
                      <Form.Item
                        name="salaryCycleDate"
                        label="Salary Cycle Date"
                      >
                        <DatePicker.RangePicker
                          placeholder={['Start date', 'End date']}
                          style={{ width: '100%', height: '40px' }}
                        />
                      </Form.Item>
                    </Col> */}
                    <Col span={12}>
                      <Form.Item
                        name="staffType"
                        label="Staff Type"
                      >
                        <Select placeholder="Select staff type" style={{ height: '40px' }}>
                          <Option value="regular">Regular</Option>
                          <Option value="contractual">Contractual</Option>
                          <Option value="intern">Intern</Option>
                          <Option value="part-time">Part Time</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="salaryTemplate"
                        label="Salary Template"
                        rules={requiredRule('Please select salary template')}
                      >
                        <Select
                          placeholder="Select salary template"
                          style={{ height: '40px' }}
                          onChange={handleSalaryTemplateChange}
                        >
                          {salaryTemplates.map(template => (
                            <Option key={template.id} value={template.id}>
                              {template.name}
                              {template.metadata?.calculationType === 'percentage' && (
                                <Tag color="blue" style={{ marginLeft: 6, fontSize: '11px' }}>%</Tag>
                              )}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="shiftSelection"
                        label="Shift Selection"
                      >
                        <Select placeholder="Select shift" style={{ height: '40px' }} allowClear>
                          {shiftTemplates.map(st => (
                            <Option key={st.id} value={st.id}>
                              {st.name} {st.shiftType === 'open' ? `(Open • ${st.workMinutes || 0}m)` : st.startTime && st.endTime ? `(${st.startTime}-${st.endTime})` : ''}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    {/* <Col span={12}>
                      <Form.Item
                        label="Opening Balance"
                      >
                        <Input.Group compact>
                          <Form.Item name="openingBalanceType" noStyle initialValue="advance">
                            <Select
                              style={{ width: '40%', height: '40px' }}
                              placeholder="Select type"
                            >
                              <Option value="advance">Advance</Option>
                              <Option value="pending">Pending</Option>
                            </Select>
                          </Form.Item>
                          <Form.Item name="openingBalanceAmount" noStyle>
                            <Input
                              style={{ width: '60%', height: '40px' }}
                              placeholder="Enter amount"
                              type="number"
                            />
                          </Form.Item>
                        </Input.Group>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="salaryDetailAccess"
                        label="Salary Detail Access"
                        valuePropName="checked"
                      >
                        <Switch checkedChildren="Yes" unCheckedChildren="No" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="allowCurrentCycleSalaryAccess"
                        label="Allow Current Cycle Salary Access"
                        valuePropName="checked"
                      >
                        <Switch checkedChildren="Yes" unCheckedChildren="No" />
                      </Form.Item>
                    </Col> */}
                  </Row>
                </Card>
              </Col>

              {/* Third Card (you mentioned 3 cards, so leaving space for the third one) */}
              <Col span={24}>
                <Card
                  title="Additional Information"
                  style={{
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Form.Item
                        name="email"
                        label="Email Address"
                        rules={[
                          { type: 'email', message: 'Please enter valid email' }
                        ]}
                      >
                        <Input placeholder="Enter email address" style={{ height: '40px' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="designation"
                        label="Designation"
                      >
                        <Input placeholder="Enter designation" style={{ height: '40px' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </div>
        );
      case 1:
        return (
          <div style={{ padding: '20px' }}>
            <Row gutter={[16, 16]}>
              {/* Earnings Section */}
              <Col span={12}>
                <Card
                  title="Earnings"
                  bordered
                  headStyle={{ fontWeight: 600, borderBottom: '1px solid #f0f0f0' }}
                  bodyStyle={{ padding: 16 }}
                  style={{
                    borderRadius: '8px',
                    background: '#fff'
                  }}
                  /* extra={<Button type="link" size="small" style={{ padding: 0, color: '#125EC9' }} onClick={() => {
                    const id = Date.now() + Math.random();
                    setExtraEarnings(v => ([...v, { id, label: '', amount: '' }]));
                    setTimeout(() => setLastAddedEarningId(id), 0);
                  }}>+ Add More</Button>} */
                >
                  <Row gutter={[16, 16]}>
                    {isPercentageTemplate && (
                      <Col span={24}>
                        <div
                          style={{
                            background: '#f0f5ff',
                            border: '1px solid #adc6ff',
                            borderRadius: 8,
                            padding: '12px 16px',
                            marginBottom: 8,
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <span style={{ fontWeight: 600, color: '#1d39c4', fontSize: '13px' }}>
                              Percentage-Based Template Active
                            </span>
                            <Tag color="blue">Auto-Calculates from Basic</Tag>
                          </div>
                          <div style={{ fontSize: 12, color: '#595959', marginBottom: 6 }}>
                            Basic Salary enter karte hi sabhi allowances uske percentage (% of Basic) ke anusaar auto-divide ho jayenge:
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {Object.entries(templatePercentages).map(([k, pct]) => {
                              const isB = k.replace(/[^A-Z]/g, '') === 'BASICSALARY';
                              if (isB || Number(pct) <= 0) return null;
                              return (
                                <Tag key={k} color="geekblue" style={{ fontSize: 11 }}>
                                  {k}: {pct}% of Basic
                                </Tag>
                              );
                            })}
                          </div>
                        </div>
                      </Col>
                    )}
                    {showE('BASIC SALARY') && (
                      <Col span={24}>
                        <Form.Item
                          name="basic_salary"
                          label={<span>Basic Salary{isPercentageTemplate && <Tag color="blue" style={{ marginLeft: 8 }}>Base Amount</Tag>}</span>}
                          rules={requiredRule('Please enter basic salary')}
                        >
                          <Input
                            type="number"
                            placeholder="Enter basic salary"
                            style={{ height: '40px' }}
                            onChange={(e) => {
                              if (isPercentageTemplate) {
                                distributeFromBasic(e.target.value);
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>
                    )}
                    {showE('HRA') && (
                      <Col span={24}>
                        <Form.Item
                          name="hra"
                          label={<span>HRA{isPercentageTemplate && templatePercentages['HRA'] !== undefined && Number(templatePercentages['HRA']) > 0 && <Tag color="blue" style={{ marginLeft: 8 }}>{templatePercentages['HRA']}% of Basic</Tag>}</span>}
                          rules={requiredRule('Please enter HRA')}
                        >
                          <Input type="number" placeholder="Enter HRA amount" style={{ height: '40px' }} />
                        </Form.Item>
                      </Col>
                    )}
                    {showE('DA') && (
                      <Col span={24}>
                        <Form.Item
                          name="da"
                          label={<span>DA{isPercentageTemplate && templatePercentages['DA'] !== undefined && Number(templatePercentages['DA']) > 0 && <Tag color="blue" style={{ marginLeft: 8 }}>{templatePercentages['DA']}% of Basic</Tag>}</span>}
                          rules={requiredRule('Please enter DA')}
                        >
                          <Input type="number" placeholder="Enter DA amount" style={{ height: '40px' }} />
                        </Form.Item>
                      </Col>
                    )}
                    {showE('SPECIAL ALLOWANCE') && (
                      <Col span={24}>
                        <Form.Item
                          name="special_allowance"
                          label={<span>Special Allowance{isPercentageTemplate && templatePercentages['SPECIAL ALLOWANCE'] !== undefined && Number(templatePercentages['SPECIAL ALLOWANCE']) > 0 && <Tag color="blue" style={{ marginLeft: 8 }}>{templatePercentages['SPECIAL ALLOWANCE']}% of Basic</Tag>}</span>}
                        >
                          <Input type="number" placeholder="Enter special allowance" style={{ height: '40px' }} />
                        </Form.Item>
                      </Col>
                    )}
                    {showE('CONVEYANCE ALLOWANCE') && (
                      <Col span={24}>
                        <Form.Item
                          name="conveyance_allowance"
                          label={<span>Conveyance Allowance{isPercentageTemplate && templatePercentages['CONVEYANCE ALLOWANCE'] !== undefined && Number(templatePercentages['CONVEYANCE ALLOWANCE']) > 0 && <Tag color="blue" style={{ marginLeft: 8 }}>{templatePercentages['CONVEYANCE ALLOWANCE']}% of Basic</Tag>}</span>}
                        >
                          <Input type="number" placeholder="Enter conveyance allowance" style={{ height: '40px' }} />
                        </Form.Item>
                      </Col>
                    )}
                    {showE('TRAVEL ALLOWANCE') && (
                      <Col span={24}>
                        <Form.Item
                          name="travel_allowance"
                          label={<span>Travel Allowance{isPercentageTemplate && templatePercentages['TRAVEL ALLOWANCE'] !== undefined && Number(templatePercentages['TRAVEL ALLOWANCE']) > 0 && <Tag color="blue" style={{ marginLeft: 8 }}>{templatePercentages['TRAVEL ALLOWANCE']}% of Basic</Tag>}</span>}
                        >
                          <Input type="number" placeholder="Enter travel allowance" style={{ height: '40px' }} />
                        </Form.Item>
                      </Col>
                    )}
                    {showE('MEDICAL ALLOWANCE') && (
                      <Col span={24}>
                        <Form.Item
                          name="medical_allowance"
                          label={<span>Medical Allowance{isPercentageTemplate && templatePercentages['MEDICAL ALLOWANCE'] !== undefined && Number(templatePercentages['MEDICAL ALLOWANCE']) > 0 && <Tag color="blue" style={{ marginLeft: 8 }}>{templatePercentages['MEDICAL ALLOWANCE']}% of Basic</Tag>}</span>}
                        >
                          <Input type="number" placeholder="Enter medical allowance" style={{ height: '40px' }} />
                        </Form.Item>
                      </Col>
                    )}
                    {showE('OTHER ALLOWANCES') && (
                      <Col span={24}>
                        <Form.Item
                          name="other_allowances"
                          label={<span>Other Allowances{isPercentageTemplate && templatePercentages['OTHER ALLOWANCES'] !== undefined && Number(templatePercentages['OTHER ALLOWANCES']) > 0 && <Tag color="blue" style={{ marginLeft: 8 }}>{templatePercentages['OTHER ALLOWANCES']}% of Basic</Tag>}</span>}
                        >
                          <Input type="number" placeholder="Enter other allowances" style={{ height: '40px' }} />
                        </Form.Item>
                      </Col>
                    )}
                    {extraEarnings.map((row, idx) => (
                      row.fromTemplate ? (
                        <Col span={24} key={row.id}>
                          <Form.Item label={row.label.toUpperCase()} style={{ marginBottom: 0 }}>
                            <Input
                              placeholder={`Enter ${row.label} amount`}
                              type="number"
                              value={row.amount}
                              onChange={(e) => {
                                const val = e.target.value;
                                setExtraEarnings(list => list.map(r => r.id === row.id ? { ...r, amount: val } : r));
                              }}
                              style={{ height: '40px' }}
                            />
                          </Form.Item>
                        </Col>
                      ) : (
                        <React.Fragment key={row.id}>
                          <Col span={12} id={`earn-${row.id}`} style={{ transition: 'background-color 0.8s', backgroundColor: row.id === lastAddedEarningId ? '#fffbe6' : 'transparent', borderRadius: 4 }}>
                            <Input
                              placeholder="Custom earning label"
                              value={row.label}
                              onChange={(e) => {
                                const val = e.target.value; setExtraEarnings(list => list.map(r => r.id === row.id ? { ...r, label: val } : r));
                              }}
                              style={{ height: '40px' }}
                              autoFocus={row.id === lastAddedEarningId}
                            />
                          </Col>
                          <Col span={10}>
                            <Input
                              placeholder="Amount"
                              type="number"
                              value={row.amount}
                              onChange={(e) => {
                                const val = e.target.value; setExtraEarnings(list => list.map(r => r.id === row.id ? { ...r, amount: val } : r));
                              }}
                              style={{ height: '40px' }}
                            />
                          </Col>
                          <Col span={2} style={{ display: 'flex', alignItems: 'center' }}>
                            <Button danger type="link" onClick={() => setExtraEarnings(list => list.filter(r => r.id !== row.id))}>
                              Remove
                            </Button>
                          </Col>
                        </React.Fragment>
                      )
                    ))}
                  </Row>
                </Card>
              </Col>

              {/* Deductions Section */}
              <Col span={12}>
                <Card
                  title="Deductions"
                  bordered
                  headStyle={{ fontWeight: 600, borderBottom: '1px solid #f0f0f0' }}
                  bodyStyle={{ padding: 16 }}
                  style={{
                    borderRadius: '8px',
                    background: '#fff'
                  }}
                  /* extra={<Button type="link" size="small" style={{ padding: 0, color: '#125EC9' }} onClick={() => {
                    const id = Date.now() + Math.random();
                    setExtraDeductions(v => ([...v, { id, label: '', amount: '' }]));
                    setTimeout(() => setLastAddedDeductionId(id), 0);
                  }}>+ Add More</Button>} */
                >
                  <Row gutter={[16, 16]}>
                    {showD('PROVIDENT FUND') && (
                      <>
                        <Col span={24}>
                          <Form.Item
                            name="provident_fund_employee"
                            label="Employee PF"
                          >
                            <InputNumber
                              min={0}
                              style={{ width: '100%', height: '40px' }}
                              placeholder="Employee PF"
                              disabled
                            />
                          </Form.Item>
                        </Col>
                      </>
                    )}
                    {showD('ESI') && (
                      <Col span={24}>
                        <Form.Item
                          name="esi"
                          label="Employee ESI"
                        >
                          <InputNumber
                            min={0}
                            style={{ width: '100%', height: '40px' }}
                            placeholder="Employee ESI"
                            disabled
                          />
                        </Form.Item>
                      </Col>
                    )}
                    {showD('PROFESSIONAL TAX') && (
                      <Col span={24}>
                        <Form.Item
                          name="professional_tax"
                          label="Professional Tax"
                        >
                          <InputNumber
                            min={0}
                            style={{ width: '100%', height: '40px' }}
                            placeholder="Professional Tax"
                            disabled
                          />
                        </Form.Item>
                      </Col>
                    )}
                    {showD('INCOME TAX') && (
                      <Col span={24}>
                        <Form.Item
                          name="income_tax"
                          label="Income Tax"
                        >
                          <Input type="number" placeholder="Enter income tax" style={{ height: '40px' }} />
                        </Form.Item>
                      </Col>
                    )}
                    {showD('LOAN DEDUCTION') && (
                      <Col span={24}>
                        <Form.Item
                          name="loan_deduction"
                          label="Loan Deduction"
                        >
                          <Input type="number" placeholder="Enter loan deduction" style={{ height: '40px' }} />
                        </Form.Item>
                      </Col>
                    )}
                    {showD('OTHER DEDUCTIONS') && (
                      <Col span={24}>
                        <Form.Item
                          name="other_deductions"
                          label="Other Deductions"
                        >
                          <Input type="number" placeholder="Enter other deductions" style={{ height: '40px' }} />
                        </Form.Item>
                      </Col>
                    )}
                    {extraDeductions.map((row) => (
                      row.fromTemplate ? (
                        <Col span={24} key={row.id}>
                          <Form.Item label={row.label.toUpperCase()} style={{ marginBottom: 0 }}>
                            <Input
                              placeholder={`Enter ${row.label} amount`}
                              type="number"
                              value={row.amount}
                              onChange={(e) => {
                                const val = e.target.value;
                                setExtraDeductions(list => list.map(r => r.id === row.id ? { ...r, amount: val } : r));
                              }}
                              style={{ height: '40px' }}
                            />
                          </Form.Item>
                        </Col>
                      ) : (
                        <React.Fragment key={row.id}>
                          <Col span={12} id={`ded-${row.id}`} style={{ transition: 'background-color 0.8s', backgroundColor: row.id === lastAddedDeductionId ? '#fffbe6' : 'transparent', borderRadius: 4 }}>
                            <Input
                              placeholder="Custom deduction label"
                              value={row.label}
                              onChange={(e) => {
                                const val = e.target.value; setExtraDeductions(list => list.map(r => r.id === row.id ? { ...r, label: val } : r));
                              }}
                              style={{ height: '40px' }}
                              autoFocus={row.id === lastAddedDeductionId}
                            />
                          </Col>
                          <Col span={10}>
                            <Input
                              placeholder="Amount"
                              type="number"
                              value={row.amount}
                              onChange={(e) => {
                                const val = e.target.value; setExtraDeductions(list => list.map(r => r.id === row.id ? { ...r, amount: val } : r));
                              }}
                              style={{ height: '40px' }}
                            />
                          </Col>
                          <Col span={2} style={{ display: 'flex', alignItems: 'center' }}>
                            <Button danger type="link" onClick={() => setExtraDeductions(list => list.filter(r => r.id !== row.id))}>
                              Remove
                            </Button>
                          </Col>
                        </React.Fragment>
                      )
                    ))}
                  </Row>
                </Card>
              </Col>
            </Row>

            {/* Salary Summary */}
            <Row gutter={[16, 16]} style={{ marginTop: '20px' }}>
              <Col span={24}>
                <Card
                  title="Salary Summary"
                  bordered
                  headStyle={{ fontWeight: 600, borderBottom: '1px solid #f0f0f0' }}
                  bodyStyle={{ padding: 16 }}
                  style={{
                    borderRadius: '8px',
                    background: '#fff'
                  }}
                >
                  <Row gutter={16}>
                    <Col span={8}>
                      <Card size="small" style={{ textAlign: 'center', background: '#f0f8ff' }}>
                        <div style={{ fontSize: '14px', color: '#666' }}>Total Earnings</div>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>
                          ₹{(calcTotalEarnings() || 0).toLocaleString('en-IN')}
                        </div>
                      </Card>
                    </Col>
                    <Col span={8}>
                      <Card size="small" style={{ textAlign: 'center', background: '#fff2f0' }}>
                        <div style={{ fontSize: '14px', color: '#666' }}>Total Deductions</div>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ff4d4f' }}>
                          ₹{(calcTotalDeductions() || 0).toLocaleString('en-IN')}
                        </div>
                      </Card>
                    </Col>
                    <Col span={8}>
                      <Card size="small" style={{ textAlign: 'center', background: '#f6ffed' }}>
                        <div style={{ fontSize: '14px', color: '#666' }}>Net Salary</div>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>
                          ₹{(calcNetSalary() || 0).toLocaleString('en-IN')}
                        </div>
                      </Card>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </div>
        );
      case 2:
        return (
          <>
            <Card title="Education Details" style={{ marginBottom: 24 }}>
              <Form.List name="education">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Row gutter={16} key={key} align="middle">
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, 'degree']}
                            label="Degree"
                            rules={requiredRule('Missing degree')}
                          >
                            <Input placeholder="MCA, B.Tech, etc." />
                          </Form.Item>
                        </Col>
                        <Col span={7}>
                          <Form.Item
                            {...restField}
                            name={[name, 'institution']}
                            label="Institution"
                            rules={requiredRule('Missing institution')}
                          >
                            <Input placeholder="University Name" />
                          </Form.Item>
                        </Col>
                        <Col span={7}>
                          <Form.Item
                            {...restField}
                            name={[name, 'year']}
                            label="Year"
                            rules={requiredRule('Missing year')}
                          >
                            <Input placeholder="2020" />
                          </Form.Item>
                        </Col>
                        <Col span={2}>
                          <Button danger type="link" onClick={() => remove(name)} icon={<DeleteOutlined />} />
                        </Col>
                      </Row>
                    ))}
                    <Form.Item>
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                        Add Education
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Card>

            <Card title="Experience Details">
              <Form.List name="experience">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Row gutter={16} key={key} align="middle">
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, 'company']}
                            label="Company"
                            rules={requiredRule('Missing company')}
                          >
                            <Input placeholder="Company Name" />
                          </Form.Item>
                        </Col>
                        <Col span={7}>
                          <Form.Item
                            {...restField}
                            name={[name, 'designation']}
                            label="Designation"
                            rules={requiredRule('Missing designation')}
                          >
                            <Input placeholder="Software Engineer" />
                          </Form.Item>
                        </Col>
                        <Col span={7}>
                          <Form.Item
                            {...restField}
                            name={[name, 'duration']}
                            label="Duration"
                            rules={requiredRule('Missing duration')}
                          >
                            <Input placeholder="2 Years" />
                          </Form.Item>
                        </Col>
                        <Col span={2}>
                          <Button danger type="link" onClick={() => remove(name)} icon={<DeleteOutlined />} />
                        </Col>
                      </Row>
                    ))}
                    <Form.Item>
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                        Add Experience
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Card>
            {!isEditing && (
              <Form.Item name="sendOfferLetter" valuePropName="checked" style={{ marginTop: 24 }}>
                <Checkbox onChange={async (e) => {
                  if (e.target.checked) {
                    try {
                      const resp = await api.get('/admin/letters/templates');
                      const templates = resp.data?.templates || [];
                      const offerTemplate = templates.find(t => (t.title || '').toLowerCase().includes('offer'));
                      if (!offerTemplate) {
                        message.error('No offer letter created. Please create offer letter first.');
                        form.setFieldsValue({ sendOfferLetter: false });
                      }
                    } catch (err) {
                      message.error('Failed to verify offer letter templates');
                      form.setFieldsValue({ sendOfferLetter: false });
                    }
                  }
                }}>
                  Send Offer Letter to Employee on creation
                </Checkbox>
              </Form.Item>
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar />

      <Layout style={{ marginLeft: 200, height: '100vh', overflow: 'hidden' }}>
        <Header style={{ padding: '0 24px', background: '#fff', display: 'flex', alignItems: 'center', position: 'sticky', top: 0, zIndex: 90 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleCancel}
            style={{ marginRight: '16px' }}
          >
            Back
          </Button>
          <Title level={4} style={{ margin: 0 }}>{isEditing ? 'Edit Regular Staff' : 'Add Regular Staff'}</Title>
        </Header>

        <Content style={{ margin: '24px 24px', padding: 24, background: '#f5f5f5', height: 'calc(100vh - 64px - 48px)', overflow: 'auto' }}>
          <Card
            style={{
              boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
              borderRadius: '4px',
              border: '1px solid #e8e8e8'
            }}
          >
            <Steps current={currentStep} style={{ marginBottom: '32px' }}>
              {steps.map(item => (
                <Step key={item.title} title={item.title} />
              ))}
            </Steps>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{ active: true }}
            >
              <div style={{ minHeight: '300px' }}>
                {renderStepContent()}
              </div>

              <div style={{ marginTop: '32px' }}>
                {currentStep > 0 && (
                  <Button style={{ marginRight: 8 }} onClick={prev}>
                    Previous
                  </Button>
                )}
                {currentStep < steps.length - 1 && (
                  <Button
                    type="primary"
                    onClick={next}
                    disabled={(currentStep === 0 && !isStep0Complete()) || (currentStep === 1 && !isStep1Complete())}
                  >
                    Next
                  </Button>
                )}
                {currentStep === steps.length - 1 && (
                  <Space style={{ float: 'right' }}>
                    <Button onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      {isEditing ? 'Update Staff' : 'Create Staff'}
                    </Button>
                  </Space>
                )}
              </div>
            </Form>
          </Card>
        </Content>
      </Layout>
    </Layout>
  );
};

export default AddRegularStaff;






